/**
 * LE CATALOGUE DES TYPES D'ALARME, POUR NOMMER CHAQUE LIGNE.
 * ============================================================================
 *
 * Une surveillance ne porte que `type_code` et `type_version` (document du
 * service, `socle/instances.py:403-404`). Son nom se lit dans la route 1
 * (`GET /alarmes-v2/type`), lue sans `_id_objet` une fois par entreprise :
 * par code et version, puis par code seul si la version n'est plus servie
 * [PROPOSITION ; NON VÉRIFIÉ que le nom ne change pas d'une version à l'autre].
 *
 * Un type absent du catalogue publié, ou un catalogue indisponible, rend
 * `null` : l'écran dit alors « Type d'alarme non disponible », jamais le code.
 */
import { defineStore } from "pinia"
import { ref } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { cleDeSession } from "@modules/alarmes-v2/stores/ouverture.store"
import { serviceAlarmesV2 } from "@modules/alarmes-v2/services/alarmes-v2.service"
import type {
    AlarmesV2NombreDuType,
    AlarmesV2TypeAlarme,
} from "@modules/alarmes-v2/types/alarmes-v2"

/** Où en est la lecture du catalogue. */
export type StatutCatalogue = "attente" | "chargement" | "pret" | "indisponible"

/** La clé d'un type à sa version. */
const cleVersion = (code: string, version: number) => `${code}@${version}`

export const useTypesStore = defineStore("alarmesV2Types", () => {
    /** Où en est la lecture. */
    const statut = ref<StatutCatalogue>("attente")

    /** L'entreprise pour laquelle le catalogue a été lu. */
    const societeLue = ref("")

    /** La clé de session de cette lecture. */
    let cleLue = ""

    /** Les noms par code et version. */
    const parVersion = ref<Record<string, string>>({})

    /** Le nom de la version la plus récente servie, par code. */
    const parCode = ref<Record<string, string>>({})

    /** Les nombres réglables (libellé, unité) par code et version (lot F3, la fiche). */
    const nombresParVersion = ref<Record<string, AlarmesV2NombreDuType[]>>({})

    /** Le type porte-t-il une astreinte, par code et version (lot F6, ligne du Basique). */
    const astreinteParVersion = ref<Record<string, boolean>>({})

    let enVol: Promise<void> | null = null
    let enVolPour = ""

    /** Range les types servis dans les deux index. */
    function indexer(types: AlarmesV2TypeAlarme[]): void {
        const versions: Record<string, string> = {}
        const codes: Record<string, { nom: string; version: number }> = {}
        const nombres: Record<string, AlarmesV2NombreDuType[]> = {}
        const astreintes: Record<string, boolean> = {}
        for (const type of types) {
            if (Array.isArray(type?.nombres)) {
                nombres[cleVersion(type.code, type.version)] = type.nombres
            }
            if (typeof type?.astreinte === "boolean") {
                astreintes[cleVersion(type.code, type.version)] = type.astreinte
            }
            if (typeof type?.nom !== "string" || type.nom === "") continue
            versions[cleVersion(type.code, type.version)] = type.nom
            const connu = codes[type.code]
            if (!connu || type.version > connu.version) {
                codes[type.code] = { nom: type.nom, version: type.version }
            }
        }
        parVersion.value = versions
        nombresParVersion.value = nombres
        astreinteParVersion.value = astreintes
        parCode.value = Object.fromEntries(
            Object.entries(codes).map(([code, v]) => [code, v.nom])
        )
    }

    /** Lit le catalogue pour l'entreprise courante, une fois. Ne rejette jamais. */
    async function charger(): Promise<void> {
        const societe = useAuthStore().companyId
        const cle = cleDeSession()
        if (!societe) {
            reinitialiser()
            return
        }
        if (cleLue === cle) return
        if (enVol && enVolPour === cle) return enVol

        indexer([])
        statut.value = "chargement"
        enVolPour = cle
        const lecture = (async () => {
            try {
                const { items } = await serviceAlarmesV2.alarmesV2ListerTypes()
                if (cleDeSession() !== cle) return
                indexer(Array.isArray(items) ? items : [])
                statut.value = "pret"
            } catch {
                if (cleDeSession() !== cle) return
                statut.value = "indisponible"
            }
            societeLue.value = societe
            cleLue = cle
        })()
        enVol = lecture
        try {
            await lecture
        } finally {
            if (enVol === lecture) {
                enVol = null
                enVolPour = ""
            }
        }
    }

    /**
     * Le nom d'un type, ou `null` s'il n'est pas au catalogue.
     * @param code - `type_code` de la surveillance
     * @param version - `type_version` de la surveillance
     */
    function nomDuType(code: string, version: number): string | null {
        return (
            parVersion.value[cleVersion(code, version)] ??
            parCode.value[code] ??
            null
        )
    }

    /**
     * Les nombres réglables d'un type À SA VERSION, ou `[]` : un libellé d'une
     * autre version pourrait nommer un autre seuil, il n'est pas emprunté.
     * @param code - `type_code` de la surveillance
     * @param version - `type_version` de la surveillance
     */
    function nombresDuType(
        code: string,
        version: number
    ): AlarmesV2NombreDuType[] {
        return nombresParVersion.value[cleVersion(code, version)] ?? []
    }

    /**
     * Le type porte-t-il une astreinte (rappels jusqu'à l'acquittement), À SA
     * VERSION ; `null` quand le catalogue ne le dit pas.
     * @param code - `type_code` de la surveillance
     * @param version - `type_version` de la surveillance
     */
    function astreinteDuType(code: string, version: number): boolean | null {
        return astreinteParVersion.value[cleVersion(code, version)] ?? null
    }

    /** Remet le store à son état de départ. */
    function reinitialiser(): void {
        statut.value = "attente"
        nombresParVersion.value = {}
        astreinteParVersion.value = {}
        societeLue.value = ""
        cleLue = ""
        parVersion.value = {}
        parCode.value = {}
        enVol = null
        enVolPour = ""
    }

    return {
        statut,
        societeLue,
        charger,
        nomDuType,
        nombresDuType,
        astreinteDuType,
        reinitialiser,
    }
})
